import { useState, useEffect, useCallback } from 'react';
import {
  BarChart3,
  DollarSign,
  Eye,
  MousePointerClick,
  Target,
  RefreshCw,
  Plus,
  Search,
  Loader2,
  TrendingUp,
  Users,
  AlertTriangle,
  CheckCircle,
  Layers,
  Megaphone,
  Image
} from 'lucide-react';
import api, {
  FacebookAdsDashboard,
  FacebookAdsCampaign,
  FacebookAdsAccount,
  FacebookAdsAdSet,
  FacebookAdsAd
} from '../services/api';
import { useClient } from '../contexts/ClientContext';

type TabType = 'overview' | 'campaigns' | 'adsets' | 'ads';

const FacebookAds = () => {
  const { selectedClientId } = useClient();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<FacebookAdsDashboard | null>(null);
  const [campaigns, setCampaigns] = useState<FacebookAdsCampaign[]>([]);
  const [adSets, setAdSets] = useState<FacebookAdsAdSet[]>([]);
  const [ads, setAds] = useState<FacebookAdsAd[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [syncing, setSyncing] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<number>(30);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getFacebookAdsDashboard(selectedClientId || undefined);
      setDashboard(data);

      // Auto-select account based on client context
      if (selectedClientId && data.accounts_list.length > 0) {
        setSelectedAccount(data.accounts_list[0].id);
      } else if (!selectedClientId && data.accounts_list.length === 1) {
        setSelectedAccount(data.accounts_list[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [selectedClientId]);

  // Reset selected account when client changes
  useEffect(() => {
    setSelectedAccount('all');
    setCampaigns([]);
    setAdSets([]);
    setAds([]);
  }, [selectedClientId]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (activeTab === 'campaigns') {
      loadCampaigns();
    } else if (activeTab === 'adsets') {
      loadAdSets();
    } else if (activeTab === 'ads') {
      loadAds();
    }
  }, [activeTab, selectedAccount, statusFilter, searchTerm, selectedClientId]);

  const loadCampaigns = async () => {
    try {
      const params: Record<string, string> = {};
      if (selectedAccount !== 'all') params.account = selectedAccount;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;

      const response = await api.getFacebookAdsCampaigns(params);
      setCampaigns(response.results || []);
    } catch (err) {
      console.error('Failed to load campaigns:', err);
    }
  };

  const loadAdSets = async () => {
    try {
      const params: Record<string, string> = {};
      if (selectedAccount !== 'all') params.account = selectedAccount;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;

      const response = await api.getFacebookAdsAdSets(params);
      setAdSets(response.results || []);
    } catch (err) {
      console.error('Failed to load ad sets:', err);
    }
  };

  const loadAds = async () => {
    try {
      const params: Record<string, string> = {};
      if (selectedAccount !== 'all') params.account = selectedAccount;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;

      const response = await api.getFacebookAdsAds(params);
      setAds(response.results || []);
    } catch (err) {
      console.error('Failed to load ads:', err);
    }
  };

  const handleSync = async (accountId: string) => {
    try {
      setSyncing(accountId);
      await api.syncFacebookAdsAccount(accountId);
      await loadDashboard();
      if (activeTab === 'campaigns') {
        await loadCampaigns();
      }
    } catch (err) {
      console.error('Failed to sync account:', err);
    } finally {
      setSyncing(null);
    }
  };

  const handleConnectAccount = async () => {
    try {
      const { auth_url } = await api.getFacebookAdsAuthUrl();
      window.location.href = auth_url;
    } catch (err) {
      console.error('Failed to get auth URL:', err);
      setError('Failed to initiate Facebook Ads connection');
    }
  };

  const formatCurrency = (value: number | string) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return `$${(num || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatNumber = (value: number | string) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return (num || 0).toLocaleString();
  };

  const formatPercent = (value: number | string) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return `${(num || 0).toFixed(2)}%`;
  };

  const getStatusClass = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'PAUSED':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'DELETED':
      case 'ARCHIVED':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const getObjectiveClass = (objective: string) => {
    switch (objective) {
      case 'OUTCOME_AWARENESS':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'OUTCOME_ENGAGEMENT':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 'OUTCOME_LEADS':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'OUTCOME_SALES':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'OUTCOME_TRAFFIC':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const getObjectiveLabel = (objective: string) => {
    const labels: Record<string, string> = {
      'OUTCOME_AWARENESS': 'Awareness',
      'OUTCOME_ENGAGEMENT': 'Engagement',
      'OUTCOME_LEADS': 'Leads',
      'OUTCOME_SALES': 'Sales',
      'OUTCOME_TRAFFIC': 'Traffic',
      'OUTCOME_APP_PROMOTION': 'App Promotion'
    };
    return labels[objective] || objective;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-black flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  const tabs: { id: TabType; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'campaigns', label: 'Campaigns' },
    { id: 'adsets', label: 'Ad Sets' },
    { id: 'ads', label: 'Ads' }
  ];

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-black">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-base font-bold text-gray-900 dark:text-white">Meta Ads</h1>
              <p className="text-gray-600 dark:text-gray-300">
                Manage and analyze your Facebook & Instagram advertising campaigns
              </p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(parseInt(e.target.value))}
                className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2"
              >
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
                <option value={60}>Last 60 days</option>
                <option value={90}>Last 90 days</option>
              </select>
              <button
                onClick={loadDashboard}
                disabled={loading}
                className="flex items-center gap-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={handleConnectAccount}
                className="flex items-center gap-2 bg-primary-500 text-black px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Connect Account
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 py-3">
        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-2 flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={loadDashboard}
              className="text-sm text-red-700 hover:text-red-900 underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* No Accounts Connected */}
        {!dashboard?.accounts_list.length && !error && (
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Megaphone className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No Facebook Ads Accounts Connected
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Connect your Facebook Ads account to start managing your campaigns
            </p>
            <button
              onClick={handleConnectAccount}
              className="flex items-center gap-2 bg-primary-500 text-black px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors mx-auto"
            >
              <Plus className="w-5 h-5" />
              Connect Facebook Ads Account
            </button>
          </div>
        )}

        {dashboard?.accounts_list.length ? (
          <>
            {/* Tabs */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 mb-2">
              <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'text-primary-500 border-b-2 border-primary-500'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Account Selector */}
            {activeTab !== 'overview' && (
              <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-2">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-gray-700 dark:text-gray-300 font-medium">Account:</label>
                    <select
                      value={selectedAccount}
                      onChange={(e) => setSelectedAccount(e.target.value)}
                      className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                    >
                      <option value="all">All Accounts</option>
                      {dashboard.accounts_list.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.name} ({account.account_id})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-gray-700 dark:text-gray-300 font-medium">Status:</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                    >
                      <option value="all">All</option>
                      <option value="ACTIVE">Active</option>
                      <option value="PAUSED">Paused</option>
                    </select>
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <>
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                        <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Total Spend</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">
                          {formatCurrency(dashboard.accounts.total_spend)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <Eye className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Impressions</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">
                          {formatNumber(dashboard.accounts.total_impressions)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                        <MousePointerClick className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Clicks</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">
                          {formatNumber(dashboard.accounts.total_clicks)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                        <Users className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Reach</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">
                          {formatNumber(dashboard.accounts.total_reach)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Secondary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Avg. CPC</span>
                      <span className="text-lg font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(dashboard.accounts.total_clicks > 0 ? dashboard.accounts.total_spend / dashboard.accounts.total_clicks : 0)}
                      </span>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Avg. CPM</span>
                      <span className="text-lg font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(dashboard.accounts.total_impressions > 0 ? (dashboard.accounts.total_spend / dashboard.accounts.total_impressions) * 1000 : 0)}
                      </span>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Avg. CTR</span>
                      <span className="text-lg font-semibold text-gray-900 dark:text-white">
                        {formatPercent(dashboard.accounts.total_impressions > 0 ? (dashboard.accounts.total_clicks / dashboard.accounts.total_impressions) * 100 : 0)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Accounts List */}
                <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Connected Accounts ({dashboard.accounts_list.length})
                    </h2>
                  </div>
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {dashboard.accounts_list.map((account) => (
                      <div key={account.id} className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                            <Megaphone className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900 dark:text-white">{account.name}</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {account.account_id}
                              {account.business_id && ` | Business: ${account.business_id}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm text-gray-600 dark:text-gray-400">Spend</p>
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {formatCurrency(account.total_spend)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600 dark:text-gray-400">Clicks</p>
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {formatNumber(account.total_clicks)}
                            </p>
                          </div>
                          <button
                            onClick={() => handleSync(account.id)}
                            disabled={syncing === account.id}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
                          >
                            <RefreshCw className={`w-4 h-4 ${syncing === account.id ? 'animate-spin' : ''}`} />
                            Sync
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Campaigns Tab */}
            {activeTab === 'campaigns' && (
              <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Campaign</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Objective</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Budget</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Spend</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Impressions</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Clicks</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">CTR</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">CPC</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {campaigns.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                            No campaigns found
                          </td>
                        </tr>
                      ) : (
                        campaigns.map((campaign) => (
                          <tr key={campaign.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900 dark:text-white">{campaign.name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">{campaign.campaign_id}</div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 text-xs rounded-full ${getStatusClass(campaign.status)}`}>
                                {campaign.status}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 text-xs rounded-full ${getObjectiveClass(campaign.objective)}`}>
                                {getObjectiveLabel(campaign.objective)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900 dark:text-white">
                              {campaign.daily_budget ? `${formatCurrency(campaign.daily_budget)}/day` : '-'}
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                              {formatCurrency(campaign.spend)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900 dark:text-white">
                              {formatNumber(campaign.impressions)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900 dark:text-white">
                              {formatNumber(campaign.clicks)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900 dark:text-white">
                              {formatPercent(campaign.ctr)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900 dark:text-white">
                              {formatCurrency(campaign.cpc)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Ad Sets Tab */}
            {activeTab === 'adsets' && (
              <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Ad Set</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Campaign</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Budget</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Spend</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Reach</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Clicks</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">CTR</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {adSets.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                            No ad sets found
                          </td>
                        </tr>
                      ) : (
                        adSets.map((adSet) => (
                          <tr key={adSet.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900 dark:text-white">{adSet.name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">{adSet.adset_id}</div>
                            </td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                              {adSet.campaign_name || '-'}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 text-xs rounded-full ${getStatusClass(adSet.status)}`}>
                                {adSet.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900 dark:text-white">
                              {adSet.daily_budget ? `${formatCurrency(adSet.daily_budget)}/day` : '-'}
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                              {formatCurrency(adSet.spend)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900 dark:text-white">
                              {formatNumber(adSet.reach)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900 dark:text-white">
                              {formatNumber(adSet.clicks)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900 dark:text-white">
                              {formatPercent(adSet.ctr)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Ads Tab */}
            {activeTab === 'ads' && (
              <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Ad</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Review</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Spend</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Impressions</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Reach</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Clicks</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">CTR</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {ads.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                            No ads found
                          </td>
                        </tr>
                      ) : (
                        ads.map((ad) => (
                          <tr key={ad.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded flex items-center justify-center">
                                  <Image className="w-5 h-5 text-gray-400" />
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900 dark:text-white">{ad.name}</div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">{ad.ad_id}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 text-xs rounded-full ${getStatusClass(ad.status)}`}>
                                {ad.status}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                ad.review_feedback === 'approved'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                  : ad.review_feedback === 'disapproved'
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                              }`}>
                                {ad.review_feedback || 'pending'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                              {formatCurrency(ad.spend)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900 dark:text-white">
                              {formatNumber(ad.impressions)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900 dark:text-white">
                              {formatNumber(ad.reach)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900 dark:text-white">
                              {formatNumber(ad.clicks)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900 dark:text-white">
                              {formatPercent(ad.ctr)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default FacebookAds;
